import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

export interface DataAccountProps {
  email?: string;
  password?: string;
}

export interface DataSchoolProps {
  name?: string;
  address?: string;
  logo?: File | string;
  havePhone?: string;
  school_instagram?: string;
  npsn_number?: string;
}

export interface DataTeamProps {
  managerName?: string;
  managerPhoneNumber?: string;
  managerEmail?: string;
  achievement?: string;
  school_coach_instagram?: string;
}

export interface DataSupporterProps {
  coordinatorName?: string;
  coordinatorPhone?: string;
  documentPhoto?: File | string;
  supporter_instagram?: string;
}

interface RegistrationFormState {
  dataAccount?: DataAccountProps;
  dataSchool?: DataSchoolProps;
  dataTeam?: DataTeamProps;
  dataSupporter?: DataSupporterProps;
  errorAccount?: DataAccountProps;
  errorTeam?: DataTeamProps;
  errorSchool?: DataSchoolProps;
  errorSupporter?: DataSupporterProps;
  setDataAccount: (data: DataAccountProps) => void;
  setDataSchool: (data: DataSchoolProps) => void;
  setDataTeam: (data: DataTeamProps) => void;
  setDataSupporter: (data: DataSupporterProps) => void;
  setErrorAccount: (data: DataAccountProps) => void;
  setErrorSchool: (data: DataSchoolProps) => void;
  setErrorTeam: (data: DataTeamProps) => void;
  setErrorSupporter: (data: DataSupporterProps) => void;
  resetRegistratioNData?: () => void;
}

export const useRegistrationForm = create<RegistrationFormState>()((set) => ({
  dataAccount: {
    email: "",
    password: "",
  },
  dataSchool: {},
  dataTeam: {},
  dataSupporter: {},
  errorAccount: {},
  errorTeam: {},
  errorSchool: {},
  errorSupporter: {},
  setDataAccount: (data: DataAccountProps) => set(() => ({ dataAccount: data })),
  setDataSchool: (data: DataSchoolProps) => set(() => ({ dataSchool: data })),
  setDataTeam: (data: DataTeamProps) => set(() => ({ dataTeam: data })),
  setDataSupporter: (data: DataSupporterProps) => set(() => ({ dataSupporter: data })),
  setErrorAccount: (data: DataAccountProps) => set(() => ({ errorAccount: data })),
  setErrorSchool: (data: DataSchoolProps) => set(() => ({ errorSchool: data })),
  setErrorTeam: (data: DataTeamProps) => set(() => ({ errorTeam: data })),
  setErrorSupporter: (data: DataSupporterProps) => set(() => ({ errorSupporter: data })),
  resetRegistrationData: () =>
    set(() => ({
      dataAccount: {},
      dataSchool: {},
      dataTeam: {},
      dataSupporter: {},
      errorAccount: {},
      errorSchool: {},
      errorTeam: {},
      errorSupporter: {},
    })),
}));

export interface DataSekolahDanceProps {
  school_name?: string;
  school_address?: string;
  npsn_number?: string;
  contact_person_name?: string;
  contact_person_number?: string;
  team_social_media?: string;
  video_url?: string;
  team_photo?: File | string;
}

export interface DataTeamDanceProps {
  name_team_member_1?: string;
  number_team_member_1?: string;
  name_team_member_2?: string;
  number_team_member_2?: string;
  name_team_member_3?: string;
  number_team_member_3?: string;
  name_team_member_4?: string;
  number_team_member_4?: string;
  name_team_member_5?: string;
  number_team_member_5?: string;
  name_team_member_6?: string;
  number_team_member_6?: string;
  name_team_member_7?: string;
  number_team_member_7?: string;
  name_team_member_8?: string;
  number_team_member_8?: string;
  name_team_member_9?: string;
  number_team_member_9?: string;
  name_team_member_10?: string;
  number_team_member_10?: string;
}

export interface RegistrationFormDanceState {
  dataAccount?: DataAccountProps;
  dataSchool?: DataSekolahDanceProps;
  dataTeam?: DataTeamDanceProps;
  errorAccount?: DataAccountProps;
  errorSchool?: DataSekolahDanceProps;
  errorTeam?: DataTeamDanceProps;
  setDataSchool: (data: DataSekolahDanceProps) => void;
  setDataAccount: (data: DataAccountProps) => void;
  setDataTeam: (data: DataTeamDanceProps) => void;
  setErrorAccount: (data: DataAccountProps) => void;
  setErrorSchool: (data: DataSekolahDanceProps) => void;
  setErrorTeam: (data: DataTeamDanceProps) => void;
}

export const useRegistrationFormDance = create<RegistrationFormDanceState>()((set) => ({
  dataAccount: {},
  dataSchool: {},
  dataTeam: {},
  errorAccount: {},
  errorSchool: {},
  errorTeam: {},
  setDataAccount: (data: DataAccountProps) => set(() => ({ dataAccount: data })),
  setDataSchool: (data: DataSekolahDanceProps) => set(() => ({ dataSchool: data })),
  setDataTeam: (data: DataTeamDanceProps) => set(() => ({ dataTeam: data })),
  setErrorAccount: (data: DataAccountProps) => set(() => ({ errorAccount: data })),
  setErrorSchool: (data: DataSekolahDanceProps) => set(() => ({ errorSchool: data })),
  setErrorTeam: (data: DataTeamDanceProps) => set(() => ({ errorTeam: data })),
}));

interface UserDataState {
  email: string;
  accessToken: string;
  registrationData: object;
  setAccessToken: (token: string) => void;
  setEmail: (email: string) => void;
  setRegistrationData: (data: object) => void;
}

export interface EventDetailProps {
  city: string;
  event_date: string;
  event_end_date: string;
  event_start_date: string;
  key_visual: string;
  key_visual_mobile: string;
  location: string;
  registration_url: string;
}

export const useUserData = create<UserDataState>()(
  persist(
    (set, get) => ({
      email: "",
      accessToken: "",
      registrationData: {},
      setEmail: (email: string) => set(() => ({ email })),
      setAccessToken: (token: string) => set(() => ({ accessToken: `Bearer ${token}` })),
      setRegistrationData: (registrationData: object) => set(() => ({ registrationData })),
    }),

    {
      name: "user-data",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export interface SchoolCategoryState {
  schoolCategory: string;
  setSchoolCategory: (category: string) => void;
}

export const useSchoolCategory = create<SchoolCategoryState>()(
  persist(
    (set) => ({
      schoolCategory: "",
      setSchoolCategory: (category: string) => set(() => ({ schoolCategory: category })),
    }),
    {
      name: "school-category-data",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export interface CitySchoolState {
  citySchool: string;
  setCitySchool: (city: string) => void;
}

export const useCitySchool = create<CitySchoolState>()(
  persist(
    (set) => ({
      citySchool: "Lampung",
      setCitySchool: (city: string) => set(() => ({ citySchool: city })),
    }),
    {
      name: "cityl-category-data",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export interface CategoryCompetitionState {
  categoryCompetition: string;
  setCategoryCompetition: (category: string) => void;
}

export const useCategoryCompetition = create<CategoryCompetitionState>()(
  persist(
    (set) => ({
      categoryCompetition: "",
      setCategoryCompetition: (category: string) => set(() => ({ categoryCompetition: category })),
    }),
    {
      name: "category-competition-data",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export interface CurrentTimelineSectionState {
  section: "registration" | "watch";
  setSection: (section: "registration" | "watch") => void;
}

export const useCurrentTimelineSection = create<CurrentTimelineSectionState>((set) => ({
  section: "registration",
  setSection: (section: "registration" | "watch") => set(() => ({ section })),
}));
